/* eslint-disable no-console */
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ApisWithoutAccessToken,
  // ApisWithoutRefreshing,
} from '../../api/lelyBackend/config';
import { store } from '../../configureStore';
import {
  resetNetworkErrorMessage,
  setNetworkError,
} from '../../reducers/networkErrorSlice';
import { setInterceptorsStatus } from '../../reducers/interceptorsSlice';
import { getFarmNameParams } from './interceptorsUtils';
// import { resetApplicationStore } from '../../common/redux/purgeStore';
// import useAuth0WithRedux from '../../auth/useAuth0WithRedux';

// Global flag to prevent multiple initializations across navigation
let globalInterceptorsInitialized = false;

// Function to reset interceptors (useful for logout)
export const resetGlobalInterceptors = () => {
  if (globalInterceptorsInitialized) {
    console.log('🔗 Resetting global interceptors');
    axiosInstance.interceptors.request.clear();
    axiosInstance.interceptors.response.clear();
    globalInterceptorsInitialized = false;
  }
};

const AxiosInterceptors = ({ children }: { children: React.ReactElement }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: auth0Loading,
  } = useAuth0();
  // const navigate = useNavigate();
  // const { logout } = useAuth0WithRedux();

  console.log('🔗 AxiosInterceptors component rendered', {
    globalInterceptorsInitialized,
    isAuthenticated,
    auth0Loading,
  });

  useEffect(() => {
    // Prevent multiple initializations across navigation
    if (globalInterceptorsInitialized) {
      return;
    }
    globalInterceptorsInitialized = true;

    const reqInteceptor = async (request) => {
      let accessToken = '';
      try {
        accessToken = await getAccessTokenSilently();
      } catch (e) {
        console.log('error', e);
        navigate('/logout');
        return Promise.reject(e);
      }
      store.dispatch(resetNetworkErrorMessage());

      const { shouldAddFarmName, farmId } = getFarmNameParams(request);
      const withAccessToken = !ApisWithoutAccessToken.find((item) => {
        return request.url?.match(item);
      });

      if (shouldAddFarmName) {
        if (request.method === 'get') {
          request.params = {
            ...request.params,
            farmId,
          };
        } else {
          request.data = {
            ...request.data,
            farmId,
          };
        }
      }

      if (accessToken && withAccessToken) {
        request.headers = {
          Authorization: `Bearer ${accessToken}`,
        };
      }

      return request;
    };

    const resInteceptor = (response) => {
      return response;
    };

    const errInteceptor = async (error: any): Promise<any> => {
      console.log(
        '🔗 Error interceptor called for:',
        error?.config?.url,
        'Status:',
        error?.response?.status,
      );

      if (error?.message === 'Network Error') {
        store.dispatch(setNetworkError(error));
        return Promise.reject(error);
      }

      // Handle 401 (Unauthorized) - user needs to re-authenticate
      if (
        error?.response?.status === 401
        // ||
        // error?.toString().includes('Unknown or invalid refresh token') ||
        // (!isAuthenticated && !auth0Loading)
      ) {
        console.log('Authentication failed (401), logging out user');
        navigate('/logout', { replace: true });
        // dispatch(resetApplicationStore());
        // logout();
        return Promise.reject(error);
      }

      // Handle 403 (Forbidden) - user is authenticated but lacks permission
      if (error?.response?.status === 403) {
        console.log(
          'Access forbidden (403) - user lacks permission for this resource',
        );
        // Don't dispatch setNetworkError for 403s to avoid triggering error saga
        // Just reject the error so the component can handle it
        // This allows showing a "Forbidden" page instead of logging out
        store.dispatch(setNetworkError(error));
        return Promise.reject(error);
      }

      // Handle other errors
      store.dispatch(setNetworkError(error));
      return Promise.reject(error);
    };

    // Add interceptors to axios instance
    axiosInstance.interceptors.request.use(reqInteceptor);
    axiosInstance.interceptors.response.use(resInteceptor, errInteceptor);

    dispatch(setInterceptorsStatus('initialized'));

    // Don't clean up interceptors on component unmount
    // They should persist across navigation
  }, [dispatch]); // Only dispatch as dependency

  return children;
};

export default AxiosInterceptors;
